import { Action } from 'redux'
import { ThunkAction } from 'redux-thunk'
import Client from '../client'
import qz from '../printing/qz'
import PrinterToaster from '../printing/toaster'
import { PrintLabelFormat, QZHIDDevice, TestPrintType } from '../printing/types'
import { detectPrintLabelFormat } from '../printing/utils'
import { setInStorage } from '../storage'
import { AppState } from '../store'
import { CurrentActions, CurrentActionTypes, PrintSettings } from './types'
import { FeatureFlagState } from '../utils/featureFlipper'

export const setCurrentHIDDevice = (device: QZHIDDevice): CurrentActionTypes => {
  setInStorage('currentHIDDevice', device)

  return {
    type: CurrentActions.SetCurrentHIDDevice,
    HIDDevice: device
  }
}

export const setTestPrintType = (testPrintType: TestPrintType): CurrentActionTypes => {
  setInStorage('testPrintType', testPrintType)

  return {
    type: CurrentActions.SetTestPrintType,
    testPrintType
  }
}

export const setCurrentPrinterType =
  (format: PrintLabelFormat): ThunkAction<void, AppState, null, Action<string>> =>
  dispatch => {
    setInStorage('currentPrinterType', format)

    dispatch({
      type: CurrentActions.SetCurrentPrinterType,
      format
    })
  }

export const setCurrentPrinter =
  (printer: string): ThunkAction<void, AppState, null, Action<string>> =>
  dispatch => {
    setInStorage('currentPrinter', printer)

    dispatch({
      type: CurrentActions.SetCurrentPrinter,
      printer
    })

    // Setting the printer should _always_ infer the format, because the format is very coupled to the printer.
    //
    // Handle this "defaulting logic" in actions instead of reducer, because we need to persist it in localStorage
    // to maintain the choice after page refresh, and you can't write to localStorage in the reducer.
    const inferredFormat = detectPrintLabelFormat(printer)
    dispatch(setCurrentPrinterType(inferredFormat)) // handles storage and Test Print defaults
  }

export const establishQZConnectionResult = (connected: boolean): CurrentActionTypes => {
  return {
    type: CurrentActions.EstablishQZConnectionResult,
    connected
  }
}

export const establishQZConnection = (): ThunkAction<void, AppState, null, Action<string>> => async dispatch => {
  dispatch({ type: CurrentActions.EstablishQZConnection })

  if (qz.websocket.isActive()) {
    dispatch(establishQZConnectionResult(true))
    return true
  }

  try {
    await qz.websocket.connect({
      host: ['localhost' /* , 'localhost.qz.io' */],
      port: { secure: [8181 /* , 8282, 8383, 8484 */], insecure: [8182 /* , 8283, 8384, 8485 */] }
    })

    qz.websocket.setClosedCallbacks(() => {
      dispatch({ type: CurrentActions.EstablishQZConnectionResult, connected: false })
      PrinterToaster.show({
        intent: 'danger',
        message: 'Printer connection lost'
      })
    })

    dispatch(establishQZConnectionResult(true))
    return true
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log(error)
    dispatch(establishQZConnectionResult(false))
    return false
  }
}

export const setProductLabelPrintSettings = (key: keyof PrintSettings, value: boolean) => {
  return {
    type: CurrentActions.SetProductLabelPrintSettings,
    key,
    value
  }
}

export const resetOrganizationSettings = (): ThunkAction<void, AppState, null, Action<string>> => async dispatch => {
  dispatch({ type: CurrentActions.RequestOrganizationSettings })
  try {
    const res = await Client.getOrganizationSettings()
    dispatch({ type: CurrentActions.RequestOrganizationSettingsSuccess, payload: res.data.settings })
  } catch (e) {
    dispatch({ type: CurrentActions.RequestOrganizationSettingsError })
  }
}

export const saveOrganizationSettings =
  (): ThunkAction<void, AppState, null, Action<string>> => async (dispatch, getState) => {
    dispatch({ type: CurrentActions.RequestSetOrganizationSettings })
    try {
      const { settings } = getState().current.organization
      const res = await Client.updateOrganizationSettings({ settings })
      dispatch({ type: CurrentActions.RequestSetOrganizationSettingsSuccess, payload: res.data.settings })
    } catch (e) {
      dispatch({ type: CurrentActions.RequestSetOrganizationSettingsError })
    }
  }

export const requestInventoryLocations = (): ThunkAction<void, AppState, null, Action<string>> => async dispatch => {
  dispatch({ type: CurrentActions.RequestInventoryLocations })

  try {
    const res = await Client.inventoryLocationIndex()
    const values = (res.data.inventoryLocations || []).map(loc => ({ value: loc.id, label: loc.name }))

    dispatch({
      type: CurrentActions.RequestInventoryLocationsSuccess,
      values
    })
  } catch (error) {
    dispatch({ type: CurrentActions.RequestInventoryLocationsError, error })
  }
}

export const setFeature = (key: string, value: FeatureFlagState) => {
  return {
    type: CurrentActions.SetFeature,
    key,
    value
  }
}
